import { skipToken } from '@tanstack/react-query';
import { trpc } from './trpc';
import { useToast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import type { OperationFilters, ActionConfig } from '@/components/subscriptions/subscription-manager/types';

// Get filter options for dropdowns
export function useFilterOptions() {
  return trpc.subscriptionManager.getFilterOptions.useQuery();
}

// Count subscriptions matching filters (lightweight, real-time feedback)
export function useSubscriptionCount(filters: OperationFilters) {
  const hasFilters = Object.keys(filters).length > 0;

  return trpc.subscriptionManager.countSubscriptions.useQuery(
    hasFilters ? { filters } : skipToken,
    {
      refetchOnWindowFocus: false,
      staleTime: 5000, // 5 second cache for real-time feel
    }
  );
}

// Preview operation (dry run) - only fetch when entering review step
export function usePreviewOperation(
  filters: OperationFilters,
  action: ActionConfig | null,
  enabled: boolean
) {
  const shouldFetch = enabled && action !== null && Object.keys(filters).length > 0;

  return trpc.subscriptionManager.previewOperation.useQuery(
    shouldFetch ? { filters, action: action! } : skipToken,
    {
      refetchOnWindowFocus: false,
    }
  );
}

// Execute operation
export function useExecuteOperation() {
  const { toast } = useToast();
  const { t } = useTranslation('subscriptions');

  return trpc.subscriptionManager.executeOperation.useMutation({
    onSuccess: (result) => {
      const removed = result.removedSinceReview.length;
      // The reviewed set and the set that is actually eligible can differ by the time the
      // merchant confirms. Saying so beats a flat "queued" that hides a narrower operation.
      if (removed > 0 || result.addedSinceReview > 0) {
        toast({
          title: t('manager.execute.driftTitle'),
          description: [
            removed > 0 ? t('manager.execute.driftRemoved', { count: removed }) : null,
            result.addedSinceReview > 0
              ? t('manager.execute.driftAdded', { count: result.addedSinceReview })
              : null,
          ]
            .filter(Boolean)
            .join(' '),
        });
        return;
      }
      toast({
        title: t('manager.execute.startedTitle'),
        description: t('manager.execute.started'),
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// Get operation status (for polling)
export function useOperationStatus(operationId: string | null) {
  return trpc.subscriptionManager.getOperationStatus.useQuery(
    operationId ? { operationId } : skipToken,
    {
      refetchInterval: (query) => {
        const data = query.state.data;
        return data?.status === 'running' || data?.status === 'pending' ? 2000 : false;
      },
    }
  );
}

// Rollback operation
export function useRollbackOperation() {
  const { toast } = useToast();

  return trpc.subscriptionManager.rollbackOperation.useMutation({
    onSuccess: () => {
      toast({
        title: 'Rollback started',
        description: 'The rollback operation has been queued.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

// List operations (audit history)
export function useListOperations(
  pagination?: { page: number; pageSize: number },
  status?: string[],
  type?: string[]
) {
  return trpc.subscriptionManager.listOperations.useQuery({
    pagination,
    status,
    type,
  });
}
